import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import app from 'flarum/common/app';
import { extend, override } from 'flarum/common/extend';
import Button from 'flarum/common/components/Button';
import TextEditor from 'flarum/common/components/TextEditor';
import Tooltip from 'flarum/common/components/Tooltip';
import classList from 'flarum/common/utils/classList';
export default function applyEditor() {
  // Lazy load the Tiptap editor bundle when rich text is enabled
  extend(TextEditor.prototype, 'oninit', function () {
    var _app$session$user;
    this._useRichTextEditor = Boolean((_app$session$user = app.session.user) == null || (_app$session$user = _app$session$user.preferences()) == null ? void 0 : _app$session$user.useRichTextEditor);
    this._richTextToggleLoading = false;
    if (!this._useRichTextEditor) return;
    this._loaders = this._loaders || [];
    this._loaders.push(() => Promise.all([import(/* webpackChunkName: 'common/tiptap/TiptapEditorDriver', webpackMode: 'lazy-once' */ './tiptap/TiptapEditorDriver'), import(/* webpackChunkName: 'common/components/TiptapMenu', webpackMode: 'lazy-once' */ './components/TiptapMenu')]).then(_ref => {
      let _ref2 = _slicedToArray(_ref, 2),
        driverModule = _ref2[0],
        menuModule = _ref2[1];
      this._TiptapEditorDriver = driverModule.default;
      this._TiptapMenu = menuModule.default;
    }));
  });
  extend(TextEditor.prototype, 'controlItems', function (items) {
    if (!app.forum.attribute('toggleRichTextEditorButton')) return;
    if (!app.session.user) return;
    const user = app.session.user;
    const buttonOnClick = async () => {
      if (this._richTextToggleLoading) return;
      const composer = this.attrs.composer;
      const currentEditor = composer.editor;
      const container = this.$('.TextEditor-editorContainer')[0];
      if (!currentEditor || !(container != null && container.isConnected)) return;
      const newValue = !this._useRichTextEditor;
      this._richTextToggleLoading = true;

      // When switching to rich text, ensure modules are loaded first
      const loadModules = newValue && !this._TiptapEditorDriver ? Promise.all([import(/* webpackChunkName: 'common/tiptap/TiptapEditorDriver', webpackMode: 'lazy-once' */ './tiptap/TiptapEditorDriver'), import(/* webpackChunkName: 'common/components/TiptapMenu', webpackMode: 'lazy-once' */ './components/TiptapMenu')]).then(_ref3 => {
        let _ref4 = _slicedToArray(_ref3, 2),
          driverModule = _ref4[0],
          menuModule = _ref4[1];
        this._TiptapEditorDriver = driverModule.default;
        this._TiptapMenu = menuModule.default;
      }) : Promise.resolve();
      try {
        var _composer$editor;
        await Promise.all([user.savePreferences({
          useRichTextEditor: newValue
        }), loadModules]);

        // Closing or replacing the composer while the request is pending invalidates this editor.
        if (!container.isConnected || composer.editor !== currentEditor) return;
        this._useRichTextEditor = newValue;
        currentEditor.destroy();
        container.replaceChildren();
        this.tiptapEditor = null;
        // Run core's build lifecycle so mentions, emoji, and upload hooks are reattached.
        this.onbuild();
        m.redraw.sync();
        (_composer$editor = composer.editor) == null || _composer$editor.focus();
      } catch (error) {
        console.error('[fof/rich-text] Unable to switch editor mode.', error);
        app.alerts.show({
          type: 'error'
        }, app.translator.trans('core.lib.error.generic_message'));
      } finally {
        this._richTextToggleLoading = false;
        m.redraw();
      }
    };
    items.add('rich-text', m(Tooltip, {
      text: app.translator.trans('fof-rich-text.lib.composer.toggle_button')
    }, m(Button, {
      icon: "fas fa-pen-fancy",
      className: classList({
        Button: true,
        'Button--icon': true,
        active: this._useRichTextEditor
      }),
      loading: this._richTextToggleLoading,
      onclick: buttonOnClick
    })), -10);
  });
  extend(TextEditor.prototype, 'toolbarItems', function (items) {
    if (!this._useRichTextEditor) return;
    if (!this._TiptapMenu || !this.tiptapEditor) return;
    const TiptapMenu = this._TiptapMenu;
    items.remove('markdown');
    items.add('richText', m(TiptapMenu, {
      editor: this.tiptapEditor
    }), 100);
  });
  extend(TextEditor.prototype, 'buildEditorParams', function (items) {
    if (!this._useRichTextEditor) return;
    items.classNames.push('Post-body');
    items.escape = () => app.composer.close();
  });
  override(TextEditor.prototype, 'buildEditor', function (original, dom) {
    // On a slow first load the editor container may not be in the DOM yet when
    // core's `onbuild` fires, leaving `dom` undefined (see flarum/framework#4657,
    // which fixes the upstream race). Constructing Tiptap without a mount target
    // skips its internal mount and later throws when the view is dereferenced,
    // which extend()'s try/catch swallows into a dead toolbar. Fall back to core's
    // BasicEditorDriver so the user still gets a working editor (issue #11).
    if (!dom) {
      console.warn('[fof/rich-text] buildEditor called without a mount target; falling back to the basic editor.');
      this.tiptapEditor = null;
      return original(dom);
    }
    if (this._useRichTextEditor && this._TiptapEditorDriver) {
      const driver = new this._TiptapEditorDriver(dom, this.buildEditorParams());
      this.tiptapEditor = driver.editor;
      return driver;
    }
    this.tiptapEditor = null;
    return original(dom);
  });
}
flarum.reg.add('fof-rich-text', 'common/applyEditor', applyEditor);flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/TiptapEditorDriver');
flarum.reg.addChunkModule('933', '525', 'fof-rich-text', 'common/components/TiptapMenu');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/extensions/DisableDataUriPaste');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/extensions/MathBlock');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/extensions/MathInline');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/extensions/RichTextKeymap');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/extensions/SpoilerBlock');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/extensions/SpoilerInline');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/extensions/Subscript');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/extensions/Superscript');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/extensions/UploadImage');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/tiptap');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/markdown/MarkdownParserBuilder');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/markdown/index');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/markdown/MarkdownSerializerBuilder');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/markdown/uploadImage');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/markdown/markdown-it/uploadImages');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/markdown/markdown-it/altText');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/markdown/markdown-it/blockSpoiler');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/markdown/markdown-it/inlineSpoilerBars');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/markdown/markdown-it/inlineSpoilerTags');
flarum.reg.addChunkModule('933', '525', 'fof-rich-text', 'common/components/CommandButton');
flarum.reg.addChunkModule('933', '525', 'fof-rich-text', 'common/components/ListButton');
flarum.reg.addChunkModule('933', '525', 'fof-rich-text', 'common/components/MarkButton');
flarum.reg.addChunkModule('933', '525', 'fof-rich-text', 'common/components/NodeTypeDropdown');
flarum.reg.addChunkModule('933', '525', 'fof-rich-text', 'common/components/InsertImageDropdown');
flarum.reg.addChunkModule('933', '525', 'fof-rich-text', 'common/components/InsertLinkDropdown');
flarum.reg.addChunkModule('933', '525', 'fof-rich-text', 'common/components/HiddenItemsDropdown');
flarum.reg.addChunkModule('933', '525', 'fof-rich-text', 'common/components/SafariModalHack');
flarum.reg.addChunkModule('933', '525', 'fof-rich-text', 'common/components/FormDropdown');flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/TiptapEditorDriver');
flarum.reg.addChunkModule('933', '525', 'fof-rich-text', 'common/components/TiptapMenu');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/extensions/DisableDataUriPaste');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/extensions/MathBlock');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/extensions/MathInline');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/extensions/RichTextKeymap');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/extensions/SpoilerBlock');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/extensions/SpoilerInline');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/extensions/Subscript');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/extensions/Superscript');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/extensions/UploadImage');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/tiptap');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/markdown/MarkdownParserBuilder');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/markdown/index');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/markdown/MarkdownSerializerBuilder');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/markdown/uploadImage');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/markdown/markdown-it/uploadImages');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/markdown/markdown-it/altText');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/markdown/markdown-it/blockSpoiler');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/markdown/markdown-it/inlineSpoilerBars');
flarum.reg.addChunkModule('131', '119', 'fof-rich-text', 'common/tiptap/markdown/markdown-it/inlineSpoilerTags');
flarum.reg.addChunkModule('933', '525', 'fof-rich-text', 'common/components/CommandButton');
flarum.reg.addChunkModule('933', '525', 'fof-rich-text', 'common/components/ListButton');
flarum.reg.addChunkModule('933', '525', 'fof-rich-text', 'common/components/MarkButton');
flarum.reg.addChunkModule('933', '525', 'fof-rich-text', 'common/components/NodeTypeDropdown');
flarum.reg.addChunkModule('933', '525', 'fof-rich-text', 'common/components/InsertImageDropdown');
flarum.reg.addChunkModule('933', '525', 'fof-rich-text', 'common/components/InsertLinkDropdown');
flarum.reg.addChunkModule('933', '525', 'fof-rich-text', 'common/components/HiddenItemsDropdown');
flarum.reg.addChunkModule('933', '525', 'fof-rich-text', 'common/components/SafariModalHack');
flarum.reg.addChunkModule('933', '525', 'fof-rich-text', 'common/components/FormDropdown');